import * as React from "react"
import { createPortal } from "react-dom"

import { LiveRegion } from "@/components/ui/live-region"

/**
 * Story 24.2b — the one shared screen-reader announcer.
 *
 * `<Announcer />` is mounted once at start-up (`App.tsx`) and holds two
 * pre-mounted, empty live regions: polite (`data-slot="announcer-polite"`)
 * and assertive (`data-slot="announcer-assertive"`). Anything announces with
 * `announce(message, { assertive })`.
 *
 * The single entry point enforces "never announce badge counts or titles":
 * an empty message, a count-only message (`3`, `99+`) and a message equal to
 * the current `document.title` are dropped (with a dev warning). A message
 * sent while no Announcer is mounted is dropped too.
 *
 * The regions are portalled to `document.body` (Story 24.3), so they stay
 * outside `#root`, which an open Sheet or Dialog makes `inert`.
 */

type Listener = (message: string, assertive: boolean) => void

const listeners = new Set<Listener>()

const COUNT_ONLY = /^\d+\+?$/

function drop(reason: string, message: string) {
  if (import.meta.env.DEV) {
    console.warn(`[announce] dropped (${reason}): ${JSON.stringify(message)}`)
  }
}

function announce(message: string, { assertive = false }: { assertive?: boolean } = {}): void {
  const text = (message ?? "").trim()
  if (!text) return drop("empty", message)
  if (COUNT_ONLY.test(text)) return drop("count only", text)
  if (typeof document !== "undefined" && text === document.title.trim()) {
    return drop("document title", text)
  }
  listeners.forEach((listener) => listener(text, assertive))
}

type RegionState = { message: string; nonce: number }

function Announcer() {
  const [polite, setPolite] = React.useState<RegionState>({ message: "", nonce: 0 })
  const [assertive, setAssertive] = React.useState<RegionState>({ message: "", nonce: 0 })

  React.useEffect(() => {
    const listener: Listener = (message, isAssertive) => {
      const set = isAssertive ? setAssertive : setPolite
      set((prev) => ({ message, nonce: prev.nonce + 1 }))
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  return createPortal(
    <>
      <LiveRegion data-slot="announcer-polite" politeness="polite" {...polite} />
      <LiveRegion data-slot="announcer-assertive" politeness="assertive" {...assertive} />
    </>,
    document.body
  )
}

// `announce` lives beside the component it feeds (one module store, one
// entry point); fast refresh of this file reloads the page, which is fine.
// eslint-disable-next-line react-refresh/only-export-components
export { announce, Announcer }
